import { IconTile, ListItem } from "@design";
import { useQuery } from "@tanstack/react-query";
import { FileText } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router";
import { ApiError, listSpaceKnowledgeOptions } from "@/api";
import { Page } from "@/components/Page";
import { PageBreadcrumbs } from "@/components/PageBreadcrumbs";
import { QuerySection } from "@/components/section";
import { WithSidePanel } from "@/components/WithSidePanel";
import { NotFoundPage } from "@/features/errors/NotFoundPage";
import { FilePreviewPanel } from "@/features/files/FilePreviewPanel";
import { spaceFile } from "@/features/files/filePreview";
import { KnowledgeTabs } from "@/features/knowledge/KnowledgeTabs";
import { useSpaceTrail } from "@/features/spaces/spaceCrumb";
import { formatRelativeTime } from "@/i18n/format";
import { useSpaceHub } from "./useSpaceHub";

/** `/spaces/:spaceUuid/knowledge/files`: the space's uploaded files (its Knowledge area is the wiki, at
 * `/knowledge`), each opening in a preview panel. Managing files stays in the old UI for now. */
export function SpaceKnowledgePage() {
  const { t } = useTranslation("spaces", { keyPrefix: "detail.knowledge" });
  const { spaceUuid = "" } = useParams();
  const hub = useSpaceHub(spaceUuid);
  const trail = useSpaceTrail(spaceUuid, hub.data?.name);
  const [previewUuid, setPreviewUuid] = useState<string | null>(null);
  const files = useQuery({
    ...listSpaceKnowledgeOptions({ path: { space_uuid: spaceUuid } }),
    select: (r) => [...r.data].sort((a, b) => b.createdAt.localeCompare(a.createdAt)),
  });
  if (hub.error instanceof ApiError && (hub.error.status === 404 || hub.error.status === 403)) {
    return <NotFoundPage />;
  }
  return (
    <>
      {trail && <PageBreadcrumbs trail={trail} current={t("pageTitle")} />}
      <WithSidePanel
        panel={
          previewUuid &&
          files.data && (
            <FilePreviewPanel
              key={previewUuid}
              files={files.data}
              fileUuid={previewUuid}
              fileFor={(file) => spaceFile(spaceUuid, file)}
              listLabel={t("previewList")}
              onOpen={setPreviewUuid}
              onClose={() => setPreviewUuid(null)}
            />
          )
        }
      >
        <Page title={t("pageTitle")} subtitle={t("pageSubtitle", { name: hub.data?.name ?? "" })}>
          <KnowledgeTabs spaceUuid={spaceUuid} active="files" />
          <QuerySection query={files} isEmpty={(list) => list.length === 0} empty={{ hint: t("empty") }}>
            {(list) => (
              <ul>
                {list.map((file, index) => (
                  <li
                    key={file.uuid}
                    aria-current={file.uuid === previewUuid || undefined}
                    className={file.uuid === previewUuid ? "rounded-lg bg-interactive-hover" : undefined}
                  >
                    <ListItem
                      flat
                      divider={index > 0}
                      leading={
                        <IconTile size="sm">
                          <FileText size={14} />
                        </IconTile>
                      }
                      description={formatRelativeTime(file.createdAt)}
                      onClick={() => setPreviewUuid(file.uuid)}
                    >
                      {file.filename}
                    </ListItem>
                  </li>
                ))}
              </ul>
            )}
          </QuerySection>
        </Page>
      </WithSidePanel>
    </>
  );
}
